// LDT-700 (épica LDT-698) · Quién es el titular de Playium y en qué versión
// está cada texto legal, en UN solo sitio.
//
// Antes la razón social, el NRT, el domicilio y el email estaban escritos a
// mano en seis documentos × tres idiomas (dieciocho copias que había que
// cambiar a la vez), y la fecha era una frase suelta de cada JSON («Última
// actualización: junio de 2026») que llegó a quedarse tres meses atrás. Ahora
// los JSON de `src/locales/legal/` interpolan `{{legalName}}`, `{{nrt}}`,
// `{{address}}`… y `LegalDocument` pinta «Vigente desde» / «Versión de» con la
// versión de `LEGAL_DOCS`. `src/test/legal-portada.test.tsx` impide que los
// datos vuelvan a escribirse a mano en los textos.
//
// La versión de un documento se sube cuando cambia lo que dice, no cuando se
// corrige una errata: es la fecha que verá quien lo acepte (LDT-706).

export const LEGAL_ENTITY = {
  legalName: "Limbo SLU",
  nrt: "L718107N",
  // Registre de Societats d'Andorra. Confirmado por David el 2026-09-28
  // (LDT-700). Si cambia, se cambia aquí y en ningún otro sitio.
  registryNumber: "23033",
  address: "Ctra de la Rabassa, Edifici Terres de Cibós, B, 1, 1. Sant Julià de Lòria AD600, Andorra",
  email: "hola@limbo.ad",
  phone: "+376 688 531",
  representative: "David Morales Gil",
} as const;

export type LegalEntityField = keyof typeof LEGAL_ENTITY;

export type LegalDocKey = "legalNotice" | "privacy" | "cookies" | "dataDeletion" | "terms" | "refunds" | "dpa" | "subprocessors" | "security" | "health_consent";

/** A quién va dirigido un documento: agrupa la portada /legal. */
export type LegalAudience = "all" | "venues" | "families";

export interface LegalDocDef {
  key: LegalDocKey;
  /** Ruta pública sin prefijo de idioma (el sitio la localiza a /ca, /en). */
  path: string;
  audience: LegalAudience;
  /** AAAA-MM-DD: «Vigente desde» y «Versión de». */
  version: string;
}

// El orden es el de la portada dentro de cada grupo. Un documento que aún no
// existe (DPA, subencargados, seguridad, información para familias) no está
// aquí, y por eso no sale su tarjeta: se añade con su ticket (LDT-701, 702,
// 710).
export const LEGAL_DOCS: readonly LegalDocDef[] = [
  { key: "legalNotice", path: "/legal-notice", audience: "all", version: "2026-09-28" },
  { key: "privacy", path: "/privacy", audience: "all", version: "2026-09-28" },
  { key: "cookies", path: "/cookies", audience: "all", version: "2026-09-28" },
  { key: "dataDeletion", path: "/data-deletion", audience: "all", version: "2026-09-28" },
  { key: "terms", path: "/terms", audience: "venues", version: "2026-09-28" },
  { key: "refunds", path: "/refunds", audience: "venues", version: "2026-09-28" },
  // LDT-701: spoke de primer nivel, no /legal/dpa (lo que cuelga de /legal/…
  // lo reescribe vercel.json a app.html con noindex).
  { key: "dpa", path: "/dpa", audience: "venues", version: "2026-09-28" },
  // LDT-702: hermanas del DPA, por el mismo motivo de ruta.
  { key: "subprocessors", path: "/subprocessors", audience: "venues", version: "2026-09-28" },
  { key: "security", path: "/security", audience: "venues", version: "2026-09-28" },
  // LDT-711: el texto que consiente la familia al dejar alergias o necesidades
  // especiales en una reserva o inscripción (casilla propia, separada de la de
  // privacidad). Es el primero del grupo «familias». Spoke de primer nivel,
  // como el DPA: lo que cuelga de /legal/… va a app.html con noindex.
  { key: "health_consent", path: "/health-consent", audience: "families", version: "2026-09-29" },
];

export const LEGAL_AUDIENCES: readonly LegalAudience[] = ["all", "venues", "families"];

/** Ruta de la portada de los textos legales. */
export const LEGAL_HUB_PATH = "/legal";

export function legalDoc(key: LegalDocKey): LegalDocDef {
  const doc = LEGAL_DOCS.find((d) => d.key === key);
  if (!doc) throw new Error(`Documento legal desconocido: ${key}`);
  return doc;
}

/** Valores de interpolación para `t()` en el namespace `legal`. */
export const LEGAL_INTERPOLATION: Record<LegalEntityField, string> = { ...LEGAL_ENTITY };

/**
 * Sustituye `{{campo}}` por el dato de la entidad. Es lo mismo que hace i18next
 * al renderizar (con `LEGAL_INTERPOLATION`), para que los tests puedan leer los
 * JSON ya resueltos sin montar React.
 */
export function interpolateLegal(text: string): string {
  return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (whole, name: string) =>
    name in LEGAL_INTERPOLATION ? LEGAL_INTERPOLATION[name as LegalEntityField] : whole,
  );
}
